import { memo, UIEvent, useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import IconButton from "./Buttons/IconButton";
import Card from "./Cards/Card";
import InspectionFormNoteModal from "./InspectionFormNoteModal";
import { Image } from "../hooks/useImageUploader";

interface CarouselProps {
  images: Image[];
  onDelete: (key: string) => void;
  onChangeDescription: (
    images: Image[],
    imageKey: string,
    description: string
  ) => void;
}

const ImageCarousel = memo(function Wrapper(props: CarouselProps) {
  const [showLeftButton, setShowLeftButton] = useState(false);
  const [showRightButton, setShowRightButton] = useState(
    props.images?.length > 2
  );
  const [isDescriptionModalVisible, setIsDescriptionModalVisible] =
    useState<boolean>(false);
  const [currentImageKey, setCurrentImageKey] = useState<string>();
  const scroll = useRef<HTMLDivElement>(null);
  const setScroll = useCallback((scrollContainer: HTMLDivElement) => {
    setShowLeftButton(scrollContainer.scrollLeft > 0);
    setShowRightButton(
      scrollContainer.scrollLeft <
        scrollContainer.scrollWidth - scrollContainer.clientWidth
    );
  }, []);
  const handleScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      const scrollContainer = event.target as HTMLDivElement;
      setScroll(scrollContainer);
    },
    [setScroll]
  );

  useEffect(() => {
    scroll?.current &&
      props?.images?.length !== undefined &&
      setScroll(scroll?.current);
  }, [setScroll, props?.images?.length]);
  return (
    <Container>
      <Title>Toegevoegd</Title>
      <CarouselContainer ref={scroll} onScroll={handleScroll}>
        {props.images
          ?.sort((a, b) => a.timestamp - b.timestamp)
          ?.map((image) => (
            <CardPreviewContainer key={image.key}>
              <CardPreview key={image.key}>
                <CardImage
                  src={image.path || image.thumbnailUrl || image.downloadUrl}
                  alt={image.key}
                  width={190}
                  height={112}
                />
                <CardControls>
                  <IconButton
                    icon="trash"
                    tooltip="Verwijder afbeelding"
                    onClick={() => props.onDelete(image.key)}
                  />
                </CardControls>
              </CardPreview>
              <CardDescriptionButton
                onClick={() => {
                  setCurrentImageKey(image.key);
                  setIsDescriptionModalVisible(true);
                }}
              >
                {image.metadata?.description || <i>Voeg een bijschrift toe</i>}
              </CardDescriptionButton>
            </CardPreviewContainer>
          ))}
      </CarouselContainer>
      <InspectionFormNoteModal
        note={
          (currentImageKey &&
            props.images.find(({ key }) => key === currentImageKey)?.metadata
              ?.description) ||
          ""
        }
        onSave={(description: string) =>
          currentImageKey &&
          props.onChangeDescription(props.images, currentImageKey, description)
        }
        title={"Bijschrift"}
        subtitle={"Voer hieronder je bijschrift in (max. 150 karakters)"}
        submitLabel={"Bevestigen"}
        show={isDescriptionModalVisible}
        onCancel={() => setIsDescriptionModalVisible(false)}
        maxLength={150}
      />
      {showRightButton && (
        <div
          style={{
            position: "absolute",
            right: -10,
            zIndex: 2,
            top: "50%",
            transform: "translateY(-50%)",
          }}
        >
          <IconButton
            iconColor="white"
            borderColor="#0075BC"
            backgroundColor="#0075BC"
            icon="rightNav"
            tooltip="Ga naar rechts"
            onClick={() => {
              scroll?.current?.scroll({
                left: scroll.current?.scrollLeft + 200,
                behavior: "smooth",
              });
            }}
          />
        </div>
      )}
      {showLeftButton && (
        <div
          style={{
            position: "absolute",
            left: -10,
            top: "50%",
            zIndex: 2,
            transform: "translateY(-50%)",
          }}
        >
          <IconButton
            iconColor="white"
            borderColor="#0075BC"
            backgroundColor="#0075BC"
            icon="leftNav"
            tooltip="Ga naar links"
            onClick={() => {
              scroll?.current?.scroll({
                left: scroll.current?.scrollLeft - 200,
                behavior: "smooth",
              });
            }}
          />
        </div>
      )}
    </Container>
  );
});

const Container = styled.div`
  position: relative;
  max-width: 400px;
  z-index: 1;
`;

const CarouselContainer = styled.div`
  display: flex;
  flex-direction: row;
  max-width: 400px;
  overflow-x: auto;
  gap: 16px;
  border-radius: 5px;
  align-content: center;
  padding-left: 4px;
  padding-right: 4px;
  padding-top: 8px;
  padding-bottom: 8px;
`;

const Title = styled.div`
  /* Tablet/Label bold */
  font-family: "Roboto";
  font-style: normal;
  font-weight: 700;
  font-size: 13px;
  line-height: 120%;

  /* or 16px */
  display: flex;
  align-items: center;
  align-self: flex-start;

  /* Donkergrijs */
  color: #212529;
`;

const CardPreviewContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const CardPreview = styled(Card)`
  position: relative; // Set to relative so absolute child elements will position relative to this
  width: 190px;
  height: 112px;
  border-radius: 5px;
  flex-shrink: 0; // This will prevent the card from shrinking when there isn't enough space
`;

const CardImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 5px;
`;

const CardControls = styled.div`
  position: absolute; // Set to absolute so it will float over the image
  bottom: 2px;
  right: 2px;
  display: flex;
  justify-content: center; // Center the controls horizontally
`;

const CardDescriptionButton = styled.div`
  border: none;
  background: none;
  cursor: pointer;
  text-align: left;
  width: 190px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2; /* number of lines to show */
`;

export default ImageCarousel;
